// Staff View V2 - Nueva versión con diseño de Host View
// Mantiene 100% funcionalidad de staff-view.js con mejor UI/UX

import {
    callCustomer as callCustomerService,
    startAttention,
    finishService,
    markNoShow,
    reactivateCustomer,
    removePreferentialStatus,
    getQueueCounts
} from '../services/queue-service.js';
import { getSellerInfo, startShift, endShift, togglePause } from '../services/seller-service.js';
import { isSellerBusy } from '../services/seller-service.js';
import { getSellerStatus, getSellerDetails, calculateTimeBreakdown, formatTime, getQueueStats } from '../services/host-service.js';
import { calculateWaitTime } from '../utils/time-calculator.js';
import { STATUS, SELLER_STATUS, HOST_COLORS, FILTER_TYPES } from '../config/constants.js';
import stateManager from '../core/state-manager.js';
import { logout } from '../core/auth-manager.js';

// Current filter
let currentFilter = FILTER_TYPES.WAITING;

/**
 * Initialize staff view v2 controls
 */
export function initStaffViewV2Controls() {
    // Shift control
    document.getElementById('staff-v2-shift-btn').addEventListener('click', handleShiftToggle);

    // Pause control
    document.getElementById('staff-v2-pause-btn')?.addEventListener('click', handlePauseToggle);

    // Logout
    document.getElementById('staff-v2-logout-btn').addEventListener('click', logout);

    // Queue action delegation
    document.getElementById('staff-v2-queue-list').addEventListener('click', handleQueueAction);

    // Filter clicks
    document.getElementById('staff-v2-filter-waiting')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.WAITING;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        updateCustomerQueueList(allCustomers);
    });

    document.getElementById('staff-v2-filter-serving')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.SERVING;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        updateCustomerQueueList(allCustomers);
    });

    document.getElementById('staff-v2-filter-aforo')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.AFORO;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        updateCustomerQueueList(allCustomers);
    });

    document.getElementById('staff-v2-filter-standby')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.STANDBY;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        updateCustomerQueueList(allCustomers);
    });

    document.getElementById('staff-v2-filter-done')?.addEventListener('click', () => {
        currentFilter = FILTER_TYPES.DONE;
        updateFilterUI();
        const allCustomers = stateManager.getState('allCustomers');
        updateCustomerQueueList(allCustomers);
    });
}

/**
 * Update filter UI - highlight active filter
 */
function updateFilterUI() {
    const filters = ['waiting', 'serving', 'aforo', 'standby', 'done'];

    filters.forEach(filterName => {
        const div = document.getElementById(`staff-v2-filter-${filterName}`);
        if (!div) return;

        const isActive = currentFilter === FILTER_TYPES[filterName.toUpperCase()];

        if (isActive) {
            // Add thick border to active filter
            div.classList.add('ring-4', 'ring-offset-2');
            const colorMap = {
                'waiting': 'ring-blue-500',
                'serving': 'ring-green-500',
                'aforo': 'ring-purple-500',
                'standby': 'ring-gray-400',
                'done': 'ring-teal-500'
            };
            div.classList.add(colorMap[filterName]);
        } else {
            // Remove ring from inactive filters
            div.classList.remove('ring-4', 'ring-offset-2', 'ring-green-500', 'ring-blue-500', 'ring-purple-500', 'ring-gray-400', 'ring-teal-500');
        }
    });
}

/**
 * Handle shift toggle (start/end)
 */
async function handleShiftToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isShiftActive = stateManager.getState('isShiftActive');

    if (!currentUser) return;

    if (isShiftActive) {
        // End shift and logout
        await endShift(currentUser.uid);
        await logout();
    } else {
        // Start shift
        await startShift(currentUser.uid);
        stateManager.setShiftActive(true);
        updateShiftAndPauseButtonsState();

        // Refresh view
        const allCustomers = stateManager.getState('allCustomers');
        updateStaffViewV2(allCustomers);
    }
}

/**
 * Handle pause toggle
 */
async function handlePauseToggle() {
    const currentUser = stateManager.getState('currentUser');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (!currentUser) return;

    const newPauseState = !isSellerPaused;
    await togglePause(currentUser.uid, newPauseState);

    stateManager.setSellerPaused(newPauseState);
    updateShiftAndPauseButtonsState();

    // Refresh view
    const allCustomers = stateManager.getState('allCustomers');
    updateStaffViewV2(allCustomers);
}

/**
 * Update shift and pause button states
 */
function updateShiftAndPauseButtonsState() {
    const shiftBtn = document.getElementById('staff-v2-shift-btn');
    const pauseBtn = document.getElementById('staff-v2-pause-btn');
    const isShiftActive = stateManager.getState('isShiftActive');
    const isSellerPaused = stateManager.getState('isSellerPaused');

    if (isShiftActive) {
        // Shift is active - show "Terminar Jornada"
        shiftBtn.innerHTML = '<span class="text-lg">🏁</span><span class="text-sm">Terminar Jornada</span>';
        shiftBtn.className = "w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
        shiftBtn.style.minHeight = '48px';

        if (pauseBtn) {
            pauseBtn.style.display = 'flex';
            if (isSellerPaused) {
                // Paused - show "Reanudar" (Resume)
                pauseBtn.innerHTML = '<span class="text-lg">▶️</span><span class="text-sm">Reanudar</span>';
                pauseBtn.className = "w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
                pauseBtn.style.minHeight = '48px';
            } else {
                // Active - show "Pausar"
                pauseBtn.innerHTML = '<span class="text-lg">⏸️</span><span class="text-sm">Pausar</span>';
                pauseBtn.className = "w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
                pauseBtn.style.minHeight = '48px';
            }
        }
    } else {
        // Shift is not active - show "Iniciar Jornada"
        shiftBtn.innerHTML = '<span class="text-lg">▶️</span><span class="text-sm">Iniciar Jornada</span>';
        shiftBtn.className = "w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2";
        shiftBtn.style.minHeight = '48px';
        if (pauseBtn) {
            pauseBtn.style.display = 'none';
        }
    }
}

/**
 * Handle queue actions (call, start attention, finish, etc.)
 */
async function handleQueueAction(e) {
    const target = e.target.closest('button');
    if (!target || !target.dataset.id) return;

    const customerId = target.dataset.id;
    const currentUser = stateManager.getState('currentUser');

    try {
        if (target.classList.contains('call-btn')) {
            await callCustomer(customerId, currentUser);
        } else if (target.classList.contains('start-attention-btn')) {
            await startAttention(customerId, currentUser.email);
        } else if (target.classList.contains('finish-btn')) {
            await finishService(customerId);
        } else if (target.classList.contains('no-show-btn')) {
            await markNoShow(customerId);
        } else if (target.classList.contains('reactivate-btn')) {
            await reactivateCustomer(customerId);
        } else if (target.classList.contains('remove-pref-btn')) {
            await removePreferentialStatus(customerId);
        }
    } catch (error) {
        console.error('Error performing queue action:', error);
        alert('Hubo un error al realizar la acción. Por favor, intenta de nuevo.');
    }
}

/**
 * Call a customer (with seller name lookup)
 */
async function callCustomer(customerId, currentUser) {
    if (!currentUser) return;

    const sellerInfo = await getSellerInfo(currentUser.uid);
    const sellerName = sellerInfo && sellerInfo.name
        ? sellerInfo.name
        : currentUser.email.split('@')[0];

    await callCustomerService(customerId, currentUser.email, sellerName);
}

/**
 * Main update function for staff view v2
 */
export function updateStaffViewV2(allCustomers) {
    const currentUser = stateManager.getState('currentUser');
    const sellers = stateManager.getState('sellersData');

    if (!currentUser) return;

    // Update email display
    document.getElementById('staff-v2-email').textContent = currentUser.email;

    // Update shift and pause buttons
    updateShiftAndPauseButtonsState();

    // Update stats
    const stats = getQueueStats(allCustomers);
    document.getElementById('staff-v2-waiting-count').textContent = stats.waiting;
    document.getElementById('staff-v2-serving-count').textContent = stats.serving;

    // Update Total / Aforo card with dual KPIs
    const activeCustomersEl = document.getElementById('staff-v2-active-customers-count');
    if (activeCustomersEl) {
        activeCustomersEl.textContent = stats.totalActiveCustomers;
    }
    document.getElementById('staff-v2-people-count').textContent = stats.totalPeople;

    // Update standby count
    const standbyCountEl = document.getElementById('staff-v2-standby-count');
    if (standbyCountEl) {
        standbyCountEl.textContent = stats.standby;
    }

    // Update done count
    const doneCountEl = document.getElementById('staff-v2-done-count');
    if (doneCountEl) {
        doneCountEl.textContent = stats.done;
    }

    // Update customer queue list
    updateCustomerQueueList(allCustomers);

    // Update seller availability list
    updateSellersList(sellers, allCustomers);
}

/**
 * Update customer queue list
 */
function updateCustomerQueueList(allCustomers) {
    const queueList = document.getElementById('staff-v2-queue-list');
    const currentUser = stateManager.getState('currentUser');

    if (!currentUser) return;

    // Filter based on current filter
    let filteredCustomers;
    switch (currentFilter) {
        case FILTER_TYPES.WAITING:
            // Show only waiting + calling (NOT serving)
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING].includes(c.status)
            );
            break;
        case FILTER_TYPES.SERVING:
            // Show only customers being served by THIS seller
            filteredCustomers = allCustomers.filter(c =>
                c.status === STATUS.SERVING && c.attendedBy === currentUser.email
            );
            break;
        case FILTER_TYPES.AFORO:
            // Show all active: waiting + calling + serving
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING, STATUS.SERVING].includes(c.status)
            );
            break;
        case FILTER_TYPES.STANDBY:
            // Show standby customers only
            filteredCustomers = allCustomers.filter(c => c.status === STATUS.STANDBY);
            break;
        case FILTER_TYPES.DONE:
            // Show completed customers from TODAY for THIS seller only
            filteredCustomers = allCustomers.filter(c => {
                if (c.status !== STATUS.DONE) return false;
                if (c.attendedBy !== currentUser.email) return false; // Solo del vendedor actual
                if (!c.finishedAt) return false;

                const finishedDate = c.finishedAt.toDate ? c.finishedAt.toDate() : new Date(c.finishedAt);
                const today = new Date();

                return finishedDate.getDate() === today.getDate() &&
                       finishedDate.getMonth() === today.getMonth() &&
                       finishedDate.getFullYear() === today.getFullYear();
            });
            break;
        default:
            filteredCustomers = allCustomers.filter(c =>
                [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING].includes(c.status)
            );
    }

    // Sort: preferential first, then by creation time
    filteredCustomers.sort((a, b) => {
        if (a.isPreferential && !b.isPreferential) return -1;
        if (!a.isPreferential && b.isPreferential) return 1;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
    });

    if (filteredCustomers.length === 0) {
        queueList.innerHTML = '<div class="text-center text-gray-500 p-8">No hay clientes en esta vista</div>';
        return;
    }

    // Check if current user is busy
    const isCurrentUserBusy = isSellerBusy(currentUser?.email, allCustomers);

    queueList.innerHTML = filteredCustomers.map((customer, index) =>
        createStaffCustomerCard(customer, index + 1, currentUser, isCurrentUserBusy)
    ).join('');
}

/**
 * Format timestamp to HH:MM
 */
function formatTimestamp(timestamp) {
    if (!timestamp) return '--:--';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

/**
 * Create customer card for staff view v2
 */
function createStaffCustomerCard(customer, position, currentUser, isCurrentUserBusy) {
    const timeBreakdown = calculateTimeBreakdown(customer);

    // Border styling for calling customers and reactivated customers
    let borderClass = customer.isPreferential ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 bg-white';
    let borderStyle = '';

    if (customer.status === STATUS.CALLING) {
        // Green pulse for calling
        borderClass = 'border-green-400 bg-green-50';
        borderStyle = 'border-l-8 border-l-green-500 animate-pulse-border';
    } else if (customer.wasReactivated) {
        // Light blue pulse for reactivated customers
        borderClass = 'border-blue-300 bg-blue-50';
        borderStyle = 'border-l-8 border-l-blue-400 animate-pulse-border-blue';
    }

    // Status badge
    let statusBadge = '';
    let statusClass = '';

    switch (customer.status) {
        case STATUS.WAITING:
            statusBadge = 'Esperando';
            statusClass = 'bg-blue-500 text-white';
            break;
        case STATUS.PRE_SERVING:
            statusBadge = 'Pre-Atención';
            statusClass = 'bg-purple-500 text-white';
            break;
        case STATUS.CALLING:
            const callerName = customer.calledByName || (customer.calledBy ? customer.calledBy.split('@')[0] : 'Vendedora');
            statusBadge = `📞 ${callerName}`;
            statusClass = 'bg-green-100 text-green-800';
            break;
        case STATUS.SERVING:
            statusBadge = 'Atendiendo';
            statusClass = 'bg-green-600 text-white';
            break;
        case STATUS.STANDBY:
            statusBadge = 'En Pausa';
            statusClass = 'bg-gray-500 text-white';
            break;
        case STATUS.DONE:
            statusBadge = 'Atendido';
            statusClass = 'bg-teal-600 text-white';
            break;
    }

    // Time information
    const registrationTime = formatTimestamp(customer.createdAt);
    const waitingTime = formatTime(timeBreakdown.waitingTime);

    // Show serving time for SERVING and DONE customers
    const servingTime = (customer.status === STATUS.SERVING || customer.status === STATUS.DONE)
        ? formatTime(timeBreakdown.servingTime)
        : null;

    // Action buttons based on status and user
    let actionButtons = '';

    const isMyCustomer = customer.attendedBy === currentUser.email || customer.calledBy === currentUser.email;
    const isCallable = [STATUS.WAITING, STATUS.PRE_SERVING].includes(customer.status);
    const canCall = isCallable && !isCurrentUserBusy;

    if (customer.status === STATUS.CALLING && isMyCustomer) {
        // Can mark as no-show or start attention
        actionButtons = `
            <button data-id="${customer.id}" class="no-show-btn px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded" title="No se presentó">⏸️ No se presentó</button>
            <button data-id="${customer.id}" class="start-attention-btn px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded" title="Iniciar atención">▶️ Iniciar</button>
        `;
    } else if (customer.status === STATUS.SERVING && isMyCustomer) {
        // Can finish service or pause
        actionButtons = `
            <button data-id="${customer.id}" class="finish-btn px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded" title="Terminar atención">✅ Terminar</button>
            <button data-id="${customer.id}" class="no-show-btn px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded" title="Pausar cliente">⏸️ Pausar</button>
        `;
    } else if (canCall) {
        // Can call customer
        actionButtons = `<button data-id="${customer.id}" class="call-btn px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded" title="Llamar">📞 Llamar</button>`;
    } else if (customer.status === STATUS.STANDBY) {
        // Can reactivate customer from standby
        actionButtons = `<button data-id="${customer.id}" class="reactivate-btn px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 rounded" title="Reactivar">↻ Reactivar</button>`;
    }

    return `
        <div class="border rounded-lg p-2 ${borderClass} ${borderStyle} hover:shadow-md transition-shadow">
            <div class="flex justify-between items-center gap-2">
                <div class="flex-1 min-w-0">
                    <!-- LINE 1: Position, Name, Star, Status -->
                    <div class="flex items-center gap-2 mb-0.5">
                        <span class="font-bold text-base">#${position}</span>
                        ${customer.isPreferential ? '<span class="text-base" title="Preferencial">⭐</span>' : ''}
                        <span class="font-semibold text-sm truncate">${customer.firstName} ${customer.lastName}</span>
                        <span class="px-1.5 py-0.5 ${statusClass} text-xs font-semibold rounded whitespace-nowrap">${statusBadge}</span>
                    </div>
                    <!-- LINE 2: Type, Reg Time, Wait Time, Serving Time, Total, Est Wait -->
                    <div class="text-xs text-gray-600 flex items-center gap-1 flex-wrap">
                        <span>${customer.attentionType}</span>
                        <span>•</span>
                        <span>📅${registrationTime}</span>
                        <span>•</span>
                        <span>⏱️${waitingTime}</span>
                        ${servingTime ? `<span>•</span><span>👟${servingTime}</span>` : ''}
                        <span>•</span>
                        <span>⏰${formatTime(timeBreakdown.totalTime)}</span>
                    </div>
                </div>
                <!-- Action Buttons -->
                <div class="flex gap-1 flex-shrink-0">
                    ${actionButtons}
                </div>
            </div>
        </div>
    `;
}

/**
 * Update sellers availability list
 */
function updateSellersList(sellers, allCustomers) {
    const sellersList = document.getElementById('staff-v2-sellers-list');

    if (!sellers || typeof sellers !== 'object') {
        sellersList.innerHTML = '<div class="text-center text-gray-500 p-4 text-sm">Cargando vendedoras...</div>';
        return;
    }

    const sellersArray = Object.values(sellers);

    // Filter to show only logged-in sellers (isOnline = true)
    const onlineSellers = sellersArray.filter(seller => seller.isOnline === true);

    if (onlineSellers.length === 0) {
        sellersList.innerHTML = '<div class="text-center text-gray-500 p-4 text-sm">No hay vendedoras activas</div>';
        return;
    }

    sellersList.innerHTML = onlineSellers.map(seller => createSellerCard(seller, allCustomers)).join('');
}

/**
 * Create seller availability card
 */
function createSellerCard(seller, allCustomers) {
    const details = getSellerDetails(seller, allCustomers);
    const status = details.status;

    let statusColor = HOST_COLORS.OFFLINE;
    let statusText = 'Offline';
    let statusBgColor = 'bg-gray-200';

    switch (status) {
        case SELLER_STATUS.AVAILABLE:
            statusColor = HOST_COLORS.AVAILABLE;
            statusText = 'Disponible';
            statusBgColor = 'bg-green-100';
            break;
        case SELLER_STATUS.BUSY:
            statusColor = HOST_COLORS.BUSY;
            statusText = 'Ocupada';
            statusBgColor = 'bg-red-100';
            break;
        case SELLER_STATUS.PAUSED:
            statusColor = HOST_COLORS.PAUSED;
            statusText = 'En Pausa';
            statusBgColor = 'bg-yellow-100';
            break;
    }

    const currentCustomerInfo = details.currentCustomer
        ? `<div class="text-xs text-gray-600 mt-1">→ ${details.currentCustomer.firstName} ${details.currentCustomer.lastName}</div>`
        : '';

    return `
        <div class="border-2 rounded-lg p-2 ${statusBgColor}" style="border-color: ${statusColor};">
            <div class="flex items-center justify-between">
                <div class="flex-1">
                    <div class="font-semibold text-sm">${seller.name || seller.email.split('@')[0]}</div>
                    <div class="text-xs font-bold" style="color: ${statusColor};">${statusText}</div>
                    ${currentCustomerInfo}
                </div>
                <div class="w-3 h-3 rounded-full" style="background-color: ${statusColor};"></div>
            </div>
        </div>
    `;
}

/**
 * Load staff seller information
 */
export async function loadStaffSellerInfo() {
    const currentUser = stateManager.getState('currentUser');

    if (!currentUser) return;

    const sellerInfo = await getSellerInfo(currentUser.uid);

    if (sellerInfo) {
        stateManager.setShiftActive(sellerInfo.isOnline || false);
        stateManager.setSellerPaused(sellerInfo.isPaused || false);
    }
}

/**
 * Display staff email in header
 */
export function displayStaffEmailV2(user) {
    if (user) {
        document.getElementById('staff-v2-email').textContent = user.email;
    }
}
